import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    loadingText: String,
    resetDelay: Number
  }

  connect() {
    this.boundHandleSubmit = this.handleSubmit.bind(this)
    this.boundReset = this.reset.bind(this)
    this.boundPreventDoubleClick = this.preventDoubleClick.bind(this)

    this.element.addEventListener("submit", this.boundHandleSubmit)
    
    // Listen for reset event from other controllers
    this.element.addEventListener("submit-protection:reset", this.boundReset)
    
    // Prevent double-click on all submit buttons
    this.submitButtons = this.submitButtonsForForm()
    this.submitButtons.forEach(button => {
      button.addEventListener("click", this.boundPreventDoubleClick)
    })
  }

  disconnect() {
    this.element.removeEventListener("submit", this.boundHandleSubmit)
    this.element.removeEventListener("submit-protection:reset", this.boundReset)
    this.submitButtons?.forEach(button => {
      button.removeEventListener("click", this.boundPreventDoubleClick)
    })
    // Cleanup any remaining listeners
    this.cleanupBeforeUnload()
  }

  submitButtonsForForm() {
    const internalButtons = Array.from(this.element.querySelectorAll('[type="submit"], button[type="submit"]'))
    if (!this.element.id) return internalButtons

    const escapedFormId = CSS.escape(this.element.id)
    const externalButtons = Array.from(
      document.querySelectorAll(
        `button[type="submit"][form="${escapedFormId}"], input[type="submit"][form="${escapedFormId}"]`
      )
    )
    return Array.from(new Set([...internalButtons, ...externalButtons]))
  }

  preventDoubleClick(event) {
    // If already submitting, prevent the click
    if (this.isSubmitting) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }
  }

  handleSubmit(event) {
    // Check if already submitting
    if (this.isSubmitting) {
      event.preventDefault()
      return false
    }

    // Dispatch event that form is starting submission
    this.element.dispatchEvent(new CustomEvent('submit-protection:start'))

    // Mark as submitting
    this.isSubmitting = true
    this.element.dataset.submitProtectionActive = 'true'

    const submitButtons = this.submitButtonsForForm()
    
    // Store original values
    this.originalButtonStates = []
    
    submitButtons.forEach(button => {
      // Store original state
      this.originalButtonStates.push({
        button: button,
        text: button.textContent || button.value,
        disabled: button.disabled,
        classes: button.className
      })
      
      // Disable the button
      button.disabled = true
      button.classList.add("opacity-50", "cursor-not-allowed")
      button.classList.remove("cursor-pointer", "hover:bg-black/90", "hover:bg-gray-50", "hover:bg-gray-800")
      
      // Change text if loading text is provided
      if (this.loadingTextValue) {
        if (button.tagName === "INPUT") {
          button.value = this.loadingTextValue
        } else {
          button.textContent = this.loadingTextValue
        }
      }
    })

    // For forms with Turbo disabled or AJAX submissions, we need to handle re-enabling
    if (this.element.dataset.turbo === "false" || this.element.dataset.remote === "true") {
      // Listen for page unload which means the form submission succeeded
      this.pageUnloadHandler = () => {
        // Page is unloading, submission was successful, don't reset buttons
        this.cleanupBeforeUnload()
      }
      window.addEventListener('beforeunload', this.pageUnloadHandler)
      
      // For non-Turbo forms, watch for DOM changes that might indicate validation errors
      // This happens when Rails returns the form with errors
      this.setupFormReplacementObserver()
      
      // Also set a safety timeout in case the page doesn't redirect
      const resetDelay = this.resetDelayValue || 10000 // Default 10 seconds
      this.resetTimeout = setTimeout(() => {
        this.resetButtons()
      }, resetDelay)
    }

    // Listen for Turbo events to reset on error
    this.setupTurboListeners()
  }

  setupFormReplacementObserver() {
    // Watch for when the form's parent is replaced (Rails validation errors)
    const formParent = this.element.parentElement
    if (!formParent) return

    this.mutationObserver = new MutationObserver((mutations) => {
      // Check if our form was replaced
      if (!document.contains(this.element) && this.isSubmitting) {
        // Form was replaced, likely with validation errors
        // Look for a new form in the same location
        const newForm = formParent.querySelector('form')
        if (newForm) {
          // Form was replaced with a new one (validation errors)
          // The new form will reinitialize its own controllers
          this.cleanupBeforeUnload()
        }
      }
    })

    // Observe the parent for child changes
    this.mutationObserver.observe(formParent, {
      childList: true,
      subtree: true
    })
  }

  setupTurboListeners() {
    // Clean up any existing listeners
    this.removeTurboListeners()

    // Listen for Turbo form submission errors
    this.turboErrorHandler = (event) => {
      if (event.detail.formSubmission?.formElement === this.element) {
        this.resetButtons()
      }
    }

    this.turboSubmitEndHandler = (event) => {
      if (event.detail.formSubmission?.formElement === this.element) {
        // If submission ended with error, reset
        if (!event.detail.success) {
          this.resetButtons()
        }
      }
    }

    // Also listen for when the page renders (might be validation errors)
    this.turboRenderHandler = (event) => {
      // If this form is still on the page after render, likely validation errors
      if (document.contains(this.element) && this.isSubmitting) {
        // Give it a moment to see if we're redirecting
        setTimeout(() => {
          if (document.contains(this.element)) {
            this.resetButtons()
          }
        }, 100)
      }
    }

    document.addEventListener("turbo:submit-end", this.turboSubmitEndHandler)
    document.addEventListener("turbo:frame-missing", this.turboErrorHandler)
    document.addEventListener("turbo:fetch-request-error", this.turboErrorHandler)
    document.addEventListener("turbo:render", this.turboRenderHandler)
  }

  removeTurboListeners() {
    if (this.turboErrorHandler) {
      document.removeEventListener("turbo:frame-missing", this.turboErrorHandler)
      document.removeEventListener("turbo:fetch-request-error", this.turboErrorHandler)
    }
    if (this.turboSubmitEndHandler) {
      document.removeEventListener("turbo:submit-end", this.turboSubmitEndHandler)
    }
    if (this.turboRenderHandler) {
      document.removeEventListener("turbo:render", this.turboRenderHandler)
    }
  }

  cleanupBeforeUnload() {
    // Clean up timers and listeners when page is unloading
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout)
      this.resetTimeout = null
    }
    if (this.pageUnloadHandler) {
      window.removeEventListener('beforeunload', this.pageUnloadHandler)
      this.pageUnloadHandler = null
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
    this.removeTurboListeners()
  }

  resetButtons() {
    // Clear timeout if exists
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout)
      this.resetTimeout = null
    }

    // Remove page unload listener if exists
    if (this.pageUnloadHandler) {
      window.removeEventListener('beforeunload', this.pageUnloadHandler)
      this.pageUnloadHandler = null
    }

    // Disconnect mutation observer if exists
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }

    // Reset all buttons to original state
    if (this.originalButtonStates) {
      this.originalButtonStates.forEach(state => {
        state.button.disabled = state.disabled
        state.button.className = state.classes
        
        if (state.button.tagName === "INPUT") {
          state.button.value = state.text
        } else {
          state.button.textContent = state.text
        }
      })
    }

    // Clear submitting flag
    this.isSubmitting = false
    this.element.dataset.submitProtectionActive = 'false'
    
    // Dispatch event that protection is ending
    this.element.dispatchEvent(new CustomEvent('submit-protection:end'))
    
    // Clean up listeners
    this.removeTurboListeners()
  }

  // Public method that can be called by other controllers
  reset() {
    this.resetButtons()
  }
}
